
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api";
import { showLoader, hideLoader } from "./loaderSlice";

export const getProductList = createAsyncThunk(
  "getProductList",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(`/getall/clinic/serviceorproducts`);

      if (res.status === 200) {
        const products = res.data.data.filter(p => p.type === "product");
        thunkAPI.dispatch(hideLoader());
        return products;
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch products");
    }
  }
);

export const createProduct = createAsyncThunk(
  "createProduct",
  async ({ category, subCategory }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/create/clinic/serviceorproduct`,
        { type: "product", category, subCategory }
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create product");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  "updateProduct",
  async ({ id, category, subCategory }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/update/serviceorproduct/${id}`,
        { type: "product", category, subCategory }
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to update product");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "deleteProduct",
  async ({ id }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(
        `/delete/clinic/serviceorproduct/${id}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return id; // Return ID to remove from list
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to delete product");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

const productSlice = createSlice({
  name: "product",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProductList.fulfilled, (state, action) => {
        state.list = action.payload ?? [];
        state.loading = false;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.list.push(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.list.findIndex((p) => p.id === updated.id);
        if (index !== -1) {
          state.list[index] = updated;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        const id = action.payload;
        state.list = state.list.filter((p) => p.id !== id);
        state.loading = false;
        state.error = null;
      })
      .addCase(getProductList.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProductList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching products.";
      });
  },
});

export default productSlice.reducer;